import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Loader2, TrendingUp, Users, DollarSign, ArrowUpRight, ArrowDownRight, Wallet, BarChart3, Award, RefreshCw } from 'lucide-react';
import { DateRangeFilter } from './ListControls';
import { Button } from './ui/button';

function StatCard({ title, value, icon: Icon, trend, trendValue, color = '#00E5FF' }) {
  return (
    <Card className="bg-[#041A3B] border-white/10">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[#5C7599] text-xs font-medium uppercase tracking-wide">{title}</p>
            <p className="text-white text-2xl font-bold mt-1 font-outfit">{value}</p>
            {trendValue && (
              <div className={`flex items-center gap-1 mt-2 text-xs ${trend === 'up' ? 'text-[#00E676]' : 'text-[#FF1744]'}`}>
                {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                <span>{trendValue}</span>
              </div>
            )}
          </div>
          <div className="p-2.5 rounded-lg" style={{ backgroundColor: `${color}15` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MiniBarChart({ data, dataKey, label, color = '#00E5FF' }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d[dataKey] || 0), 1);
  const recent = data.slice(-14); // Last 14 days

  return (
    <div>
      <p className="text-[#A0B4CC] text-sm font-medium mb-3">{label}</p>
      <div className="flex items-end gap-1 h-24">
        {recent.map((d, i) => {
          const height = ((d[dataKey] || 0) / max) * 100;
          return (
            <div key={d.date || `bar-${i}`} className="flex-1 flex flex-col items-center gap-1 group relative">
              <div
                className="w-full rounded-sm transition-all hover:opacity-80"
                style={{ height: `${Math.max(height, 2)}%`, backgroundColor: color, minHeight: '2px' }}
              />
              <div className="hidden group-hover:block absolute -top-8 bg-[#000B18] border border-white/20 rounded px-2 py-1 text-[10px] text-white whitespace-nowrap z-10">
                {d.date?.substring(5)}: {d[dataKey] || 0}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-[#5C7599] text-[10px]">{recent[0]?.date?.substring(5)}</span>
        <span className="text-[#5C7599] text-[10px]">{recent[recent.length - 1]?.date?.substring(5)}</span>
      </div>
    </div>
  );
}

export function AdminAnalyticsPage() {
  const { api } = useAuth();
  const [overview, setOverview] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [topReferrers, setTopReferrers] = useState([]);
  const [planDist, setPlanDist] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchData = useCallback(async (isInitial = false) => {
    if (isInitial) setInitialLoading(true);
    setRefreshing(true);
    try {
      let ovUrl = '/api/admin/analytics/overview';
      const params = [];
      if (dateFrom) params.push(`date_from=${encodeURIComponent(dateFrom)}`);
      if (dateTo) params.push(`date_to=${encodeURIComponent(dateTo)}`);
      if (params.length > 0) ovUrl += '?' + params.join('&');

      const [ovRes, grRes, rvRes, trRes, pdRes] = await Promise.all([
        api.get(ovUrl),
        api.get('/api/admin/analytics/growth?days=30'),
        api.get('/api/admin/analytics/revenue-chart?days=30'),
        api.get('/api/admin/analytics/top-referrers?limit=5'),
        api.get('/api/admin/analytics/plan-distribution')
      ]);
      setOverview(ovRes.data);
      setGrowth(grRes.data.growth || []);
      setRevenue(rvRes.data.chart || []);
      setTopReferrers(trRes.data.top_referrers || []);
      setPlanDist(pdRes.data.distribution || []);
    } catch (e) {
      console.error('Analytics load failed:', e);
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, [api, dateFrom, dateTo]);

  useEffect(() => { fetchData(initialLoading); }, [fetchData]);

  const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0 }).format(n || 0);
  const num = (n) => new Intl.NumberFormat('en-PH').format(n || 0);

  if (initialLoading) return (
    <AdminSidebarLayout>
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
      </div>
    </AdminSidebarLayout>
  );

  const ov = overview || {};

  return (
    <AdminSidebarLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="analytics-dashboard">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">Analytics Dashboard</h1>
            <p className="text-[#A0B4CC] mt-1">Platform performance and insights</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {refreshing && <Loader2 className="w-4 h-4 animate-spin text-[#00E5FF]" />}
            <Button onClick={() => fetchData(false)} variant="ghost" className="text-[#00E5FF]"><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
          </div>
        </div>

        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onDateFromChange={setDateFrom} onDateToChange={setDateTo} />

        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Users" value={num(ov.users?.total)} icon={Users} color="#00E5FF"
            trend="up" trendValue={`+${ov.users?.new_7d || 0} this week`} />
          <StatCard title="Active Subscriptions" value={num(ov.subscriptions?.active)} icon={TrendingUp} color="#00E676"
            trendValue={`${ov.users?.conversion_rate || 0}% conversion`} trend="up" />
          <StatCard title="Total Revenue" value={fmt(ov.revenue?.total)} icon={DollarSign} color="#FFEA00"
            trend="up" trendValue={`${fmt(ov.revenue?.last_30_days)} last 30d`} />
          <StatCard title="Total Withdrawn" value={fmt(ov.withdrawals?.total_withdrawn)} icon={Wallet} color="#FF1744"
            trendValue={`${ov.withdrawals?.pending || 0} pending`} trend="down" />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="p-5">
              <MiniBarChart data={growth} dataKey="new_users" label="New Users (30 days)" color="#00E5FF" />
            </CardContent>
          </Card>
          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="p-5">
              <MiniBarChart data={revenue} dataKey="revenue" label="Revenue (30 days)" color="#00E676" />
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Subscription Distribution */}
          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-4 h-4 text-[#007AFF]" />
                <p className="text-[#A0B4CC] text-sm font-medium">Plan Distribution</p>
              </div>
              {planDist.length === 0 ? (
                <p className="text-[#5C7599] text-sm">No active subscriptions yet</p>
              ) : (
                <div className="space-y-3">
                  {planDist.map((p, i) => {
                    const total = planDist.reduce((a, b) => a + b.count, 0);
                    const pct = total > 0 ? (p.count / total * 100) : 0;
                    const colors = ['#00E5FF', '#00E676', '#FFEA00', '#FF1744', '#007AFF'];
                    return (
                      <div key={p.plan || `plan-${i}`}>
                        <div className="flex justify-between mb-1">
                          <span className="text-white text-sm">{p.plan}</span>
                          <span className="text-[#5C7599] text-xs">{p.count} ({pct.toFixed(0)}%)</span>
                        </div>
                        <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: colors[i % colors.length] }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Referrers */}
          <Card className="bg-[#041A3B] border-white/10 lg:col-span-2">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-4 h-4 text-[#FFEA00]" />
                <p className="text-[#A0B4CC] text-sm font-medium">Top Referrers</p>
              </div>
              {topReferrers.length === 0 ? (
                <p className="text-[#5C7599] text-sm">No referral earnings yet</p>
              ) : (
                <div className="space-y-2">
                  {topReferrers.map((r, i) => (
                    <div key={r.email || r.name || `ref-${i}`} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                          i === 0 ? 'bg-[#FFEA00]/20 text-[#FFEA00]' : i === 1 ? 'bg-[#C0C0C0]/20 text-[#C0C0C0]' : 'bg-white/10 text-[#A0B4CC]'
                        }`}>
                          #{i + 1}
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{r.name}</p>
                          <p className="text-[#5C7599] text-xs">{r.referral_code} &middot; {r.commission_count} commissions</p>
                        </div>
                      </div>
                      <p className="text-[#00E676] font-mono font-semibold text-sm">{fmt(r.total_earnings)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Extra Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Verified Users" value={num(ov.users?.verified)} icon={Users} color="#007AFF" />
          <StatCard title="Lifetime Subs" value={num(ov.subscriptions?.lifetime)} icon={TrendingUp} color="#00E676" />
          <StatCard title="Managed Value" value={fmt(ov.wallet?.total_managed_value)} icon={Wallet} color="#00E5FF" />
          <StatCard title="Referral Earnings" value={fmt(ov.referrals?.total_value)} icon={Award} color="#FFEA00" />
        </div>

        {/* Registration Funnel */}
        <RegistrationFunnelCard />
      </div>
    </AdminSidebarLayout>
  );
}

function RegistrationFunnelCard() {
  const { api } = useAuth();
  const [data, setData] = React.useState(null);
  const [days, setDays] = React.useState(7);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);
    api.get(`/api/admin/analytics/registration-funnel?days=${days}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days, api]);

  const STEP_LABELS = {
    'register_page_view': 'Page viewed',
    'field_focus:name': 'Tapped Name',
    'field_focus:email': 'Tapped Email',
    'field_focus:birthdate': 'Tapped Birthdate',
    'field_focus:mobile': 'Tapped Mobile',
    'field_focus:address': 'Tapped Address',
    'step1_complete': 'Step 1 complete',
    'register_submit_attempt': 'Submitted',
    'register_submit_success': 'Registered ✅',
  };

  return (
    <Card className="bg-[#041A3B] border-white/10" data-testid="funnel-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <h3 className="font-outfit text-lg sm:text-xl text-white">Registration Funnel</h3>
            <p className="text-[#A0B4CC] text-sm">Where signups stall on /register — anonymous, no PII.</p>
          </div>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="bg-[#000B18] border border-white/20 text-white text-sm rounded-md px-3 py-1.5"
            data-testid="funnel-days-select"
          >
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
        {loading ? (
          <p className="text-[#5C7599] text-sm py-8 text-center">Loading…</p>
        ) : !data ? (
          <p className="text-[#5C7599] text-sm py-8 text-center">No funnel data yet — it&apos;ll populate as visitors land on /register.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              <Tile label="Visitors" value={data.visitors} testId="funnel-visitors" />
              <Tile label="Registered" value={data.completed} color="text-[#00E676]" testId="funnel-completed" />
              <Tile label="Conversion" value={`${data.overall_conversion_pct}%`} color="text-[#00E5FF]" testId="funnel-conversion" />
              <Tile label="Member / Merchant" value={`${data.tab_split.member} / ${data.tab_split.merchant}`} testId="funnel-tab-split" />
            </div>
            <div className="space-y-2">
              {(data.steps || []).map((s, i) => {
                const max = data.visitors || 1;
                const pct = Math.max(0, Math.min(100, (s.sessions / max) * 100));
                const isDrop = s.drop_pct_from_previous != null && s.drop_pct_from_previous >= 30;
                return (
                  <div key={s.step} className="flex items-center gap-3" data-testid={`funnel-step-${i}`}>
                    <span className="w-44 text-sm text-[#A0B4CC] shrink-0">{STEP_LABELS[s.step] || s.step}</span>
                    <div className="flex-1 h-6 rounded bg-[#000B18] border border-white/10 overflow-hidden relative">
                      <div className={`h-full ${isDrop ? 'bg-[#FF1744]/40' : 'bg-[#00E5FF]/40'}`} style={{ width: `${pct}%` }} />
                      <span className="absolute inset-0 flex items-center px-2 text-xs text-white font-medium">
                        {s.sessions}
                      </span>
                    </div>
                    <span className={`w-16 text-right text-xs ${isDrop ? 'text-[#FF1744]' : 'text-[#5C7599]'}`}>
                      {s.drop_pct_from_previous == null ? '—' : `-${s.drop_pct_from_previous}%`}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-[#5C7599] text-[11px] mt-4">
              Red bars = drop-off ≥30% from the previous step. Field-focus events fire once per session.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Tile({ label, value, color, testId }) {
  return (
    <div className="rounded-lg bg-[#000B18] border border-white/10 p-3" data-testid={testId}>
      <p className="text-[#5C7599] text-xs">{label}</p>
      <p className={`text-lg font-semibold ${color || 'text-white'}`}>{value}</p>
    </div>
  );
}
